import { omit } from 'lodash'
import { merge } from 'lodash/fp'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { toast } from '@wellesley/system-toast'
import { getStore, RootState } from '@wellesley/store'
import { getErrorMsg } from '@wellesley/error-kit'
import { api, Event } from '@wellesley/openapi'
import { handleBasicResponse } from '@wellesley/openapi/helpers'
import { eventsSlice } from '../slices'
import { EventsSliceState } from '../slices/eventsSlice.utils'

type ExtendedRootState = RootState & {
  events: EventsSliceState
}

type EventPatcher = (evt: Event) => Event | Partial<Event>
type EventsListArgs = {
  offset?: number | null
}
type EventsListQueryStartedApi = {
  dispatch: (...args: unknown[]) => unknown
  getState: () => unknown
  queryFulfilled: Promise<{ data: Event[] }>
}

export const updateEventCache = (
  eventId: string,
  patch: Event | Partial<Event> | EventPatcher,
) => {
  getStore().dispatch(
    eventsApi.util.updateQueryData(
      'getApiV1EventsByEventId',
      { eventId },
      (draft) => {
        if (eventId === draft?.id) {
          const newDraft = typeof patch === 'function' ? patch(draft) : patch
          return merge(draft, newDraft)
        }
      },
    ),
  )
}

export const invalidateEventCache = (eventId: string) => {
  getStore().dispatch(
    eventsApi.util.invalidateTags([{ type: 'Event', id: eventId }]),
  )
}

const handleEventsListQueryStarted = async (
  args: EventsListArgs,
  { dispatch, getState, queryFulfilled }: EventsListQueryStartedApi,
) => {
  const currentScope = (getState() as ExtendedRootState).events.configs.scope

  try {
    const events = await queryFulfilled

    if (currentScope) {
      dispatch(
        eventsSlice.actions.addEvents({
          scope: currentScope,
          events: events.data,
          isNewList: args.offset === 0,
        }),
      )
    }
  } catch (e) {
    toast.error(
      `Failed to fetch events ${getErrorMsg(e as FetchBaseQueryError)}`,
    )
  }
}

const INITIAL_DATA_KEYS = {
  event: 'event',
}

export const eventsApi = api.enhanceEndpoints({
  endpoints: {
    getApiV1Events: {
      forceRefetch: () => true,
      onQueryStarted: (args, api) => handleEventsListQueryStarted(args, api),
    },
    getApiV1EventsMy: {
      forceRefetch: () => true,
      onQueryStarted: (args, api) => handleEventsListQueryStarted(args, api),
    },
    getApiV1EventsSearch: {
      forceRefetch: () => true,
      onQueryStarted: (args, api) => handleEventsListQueryStarted(args, api),
    },

    getApiV1EventsByEventId: {
      providesTags: (_result, _error, { eventId }) => [
        { type: 'Event', id: eventId },
      ],
      extraOptions: {
        maxRetries: 1,
        initialDataKey: INITIAL_DATA_KEYS.event,
      },
      // Use only { eventId } for cache key
      serializeQueryArgs: ({ endpointName, queryArgs }) => ({
        endpointName,
        eventId: queryArgs.eventId,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },

    postApiV1Events: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled, 'Event created')
      },
    },
    putApiV1EventsByEventId: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled, 'Event updated')
      },
    },
    deleteApiV1EventsByEventId: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled, 'Event deleted')
      },
    },
    postApiV1EventsByEventIdCancel: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled, 'Event cancelled')
      },
    },

    postApiV1EventsByEventIdAccept: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1EventsByEventIdReject: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1EventsByEventIdMaybe: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },

    postApiV1EventsByEventIdJoin: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1EventsByEventIdInterested: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    deleteApiV1EventsByEventIdJoinOrInterested: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },

    postApiV1EventsByEventIdTranslate: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const res = await handleBasicResponse(queryFulfilled)
        const translation = res?.data

        if (translation) {
          const patch: Partial<Event> = {
            translation,
          }

          dispatch(
            eventsSlice.actions.updateEvent({
              id: args.eventId,
              patch,
            }),
          )
          updateEventCache(args.eventId, patch)
        }
      },
    },

    // Attendees
    getApiV1EventsByEventIdAttendees: {
      providesTags: (_result, _error, { eventId }) => [
        { type: 'EventAttendees', id: eventId },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    getApiV1EventsByEventIdMutual: {
      providesTags: (_result, _error, { eventId }) => [
        { type: 'EventMutualAttendees', id: eventId },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },

    // Settings
    getApiV1EventsSettings: {
      async onQueryStarted(_, { queryFulfilled }) {
        await handleBasicResponse(queryFulfilled)
      },
    },
    postApiV1EventsSettingsUpdate: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const res = await queryFulfilled
          dispatch(
            api.util.updateQueryData(
              'getApiV1EventsSettings',
              omit(args, 'updateEventsSettings'),
              (draft) => {
                Object.assign(draft, merge(draft, res.data))
              },
            ),
          )
        } catch (e) {
          toast.error(
            `Failed to update settings ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
    postApiV1EventsSettingsRegeneratePrivateId: {
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const res = await queryFulfilled
          dispatch(
            api.util.updateQueryData(
              'getApiV1EventsSettings',
              args,
              (draft) => {
                draft.exportPrivateCalendarUniqueId = res?.data?.slice(1, -1) // cutting quotes
              },
            ),
          )
        } catch (e) {
          toast.error(
            `Failed to regenerate URL ${getErrorMsg(e as FetchBaseQueryError)}`,
          )
        }
      },
    },
  },
})

export const {
  useLazyGetApiV1EventsQuery,
  useLazyGetApiV1EventsMyQuery,
  useLazyGetApiV1EventsSearchQuery,

  useGetApiV1EventsByEventIdQuery,

  usePostApiV1EventsMutation,
  usePutApiV1EventsByEventIdMutation,
  useDeleteApiV1EventsByEventIdMutation,
  usePostApiV1EventsByEventIdCancelMutation,

  usePostApiV1EventsByEventIdAcceptMutation,
  usePostApiV1EventsByEventIdRejectMutation,
  usePostApiV1EventsByEventIdMaybeMutation,

  usePostApiV1EventsByEventIdJoinMutation,
  usePostApiV1EventsByEventIdInterestedMutation,
  useDeleteApiV1EventsByEventIdJoinOrInterestedMutation,

  usePostApiV1EventsByEventIdTranslateMutation,

  // Attendees
  useGetApiV1EventsByEventIdAttendeesQuery,
  useGetApiV1EventsByEventIdMutualQuery,

  // Settings
  useGetApiV1EventsSettingsQuery,
  usePostApiV1EventsSettingsUpdateMutation,
  usePostApiV1EventsSettingsRegeneratePrivateIdMutation,
} = eventsApi
